import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, ArrowUpRight, CalendarClock } from 'lucide-react';
import { api } from '../lib/api';
import SEOHead from '../components/common/SEOHead';

/**
 * Book a Listing Consultation — the seller-side "Strategy Session".
 * Qualifying form: timeline, motivation, current mortgage, preferred time.
 * No listing agreement discussed on the first visit.
 */
export default function ListingConsultation() {
    const [state, setState] = useState('form'); // 'form' | 'sending' | 'done' | 'error'
    const [err, setErr] = useState('');
    const [form, setForm] = useState({
        name: '', email: '', phone: '',
        address: '', city: '',
        property_type: 'detached',
        timeline: '3-6-months',
        motivation: '',
        current_mortgage: 'prefer-not',
        preferred_time: 'weekday-evening',
        notes: '',
    });

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    async function submit(e) {
        e.preventDefault();
        setState('sending');
        setErr('');
        try {
            const payload = { ...form };
            if (!payload.notes) delete payload.notes;
            await api.post('/sellers/listing-consultation', payload);
            setState('done');
        } catch (e2) {
            setErr(e2?.response?.data?.detail || 'Something went wrong. Please try again.');
            setState('error');
        }
    }

    if (state === 'done') return <DoneState name={form.name} />;

    return (
        <div className="bg-white min-h-screen">
            <SEOHead
                title="Book a Listing Consultation — Southern Ontario"
                description="Thirty minutes with an independent analyst. Comparable sales, the honest ceiling, and a go-to-market plan. No listing agreement discussed on the first visit."
                path="/sellers/consultation"
            />

            <div className="max-w-[1200px] mx-auto px-6 lg:px-10 pt-24 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
                <div className="lg:col-span-6">
                    <div className="flex items-center gap-3 text-[11px] tracking-[0.24em] uppercase text-neutral-500 font-mono-data">
                        <span className="inline-block w-6 h-px bg-neutral-400" />
                        Listing Consultation · Southern Ontario
                    </div>
                    <h1 className="font-editorial mt-6 leading-[1.05] text-black" style={{ fontSize: 'clamp(40px, 6vw, 76px)', letterSpacing: '-0.03em' }} data-testid="lc-title">
                        Thirty minutes.
                        <br />
                        <span className="italic text-neutral-500">No listing agreement discussed.</span>
                    </h1>
                    <p className="mt-6 font-editorial text-neutral-700" style={{ fontSize: '19px', lineHeight: 1.55 }}>
                        The first visit is for you. We&apos;ll walk through the last twelve sales
                        on your block, the honest ceiling, and the go-to-market plan that hits
                        it. If you like the plan, we can talk about the listing on a second
                        visit. If not, the analysis is yours to keep.
                    </p>
                    <ul className="mt-10 space-y-4 text-[14.5px] text-neutral-800">
                        {[
                            'Comparable sales — the twelve that actually matter for your home',
                            'Realistic pricing range with the trade-offs at each end',
                            'Marketing plan tailored to your segment (offer day vs open offers)',
                            'Prep, staging, and renovation ROI specific to your home',
                            'Answers to every question you have. Bring a notebook.',
                        ].map((l, i) => (
                            <li key={i} className="flex gap-3">
                                <CheckCircle2 className="w-4 h-4 mt-0.5 text-[#2F6B47] shrink-0" />
                                <span>{l}</span>
                            </li>
                        ))}
                    </ul>
                    <div className="mt-10 text-[12.5px] text-neutral-500 leading-relaxed border-t border-neutral-200 pt-6 max-w-md">
                        In-person across Durham, Hamilton, Halton, Brantford, Brant County and
                        Niagara — or on video anywhere.
                    </div>
                </div>

                <div className="lg:col-span-6">
                    <motion.form
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        onSubmit={submit}
                        className="border border-neutral-300 bg-white p-6 lg:p-8"
                       
                        data-testid="lc-form"
                    >
                        <div className="flex items-center gap-2 text-[10.5px] tracking-[0.24em] uppercase text-neutral-500 font-mono-data">
                            <CalendarClock className="w-3 h-3" /> Book the consultation
                        </div>
                        <div className="font-editorial mt-3 leading-tight" style={{ fontSize: '26px', letterSpacing: '-0.01em' }}>
                            A little context helps us prepare.
                        </div>

                        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <Field label="Full name" required>
                                <input required data-testid="lc-name" value={form.name} onChange={set('name')} className={inputCls} />
                            </Field>
                            <Field label="Email" required>
                                <input required type="email" data-testid="lc-email" value={form.email} onChange={set('email')} className={inputCls} />
                            </Field>
                            <Field label="Phone" required>
                                <input required data-testid="lc-phone" value={form.phone} onChange={set('phone')} className={inputCls} />
                            </Field>
                            <Field label="City" required>
                                <input required data-testid="lc-city" value={form.city} onChange={set('city')} className={inputCls} placeholder="Hamilton, Oakville…" />
                            </Field>
                            <Field label="Street address" required className="sm:col-span-2">
                                <input required data-testid="lc-address" value={form.address} onChange={set('address')} className={inputCls} />
                            </Field>
                            <Field label="Property type" required>
                                <select required data-testid="lc-type" value={form.property_type} onChange={set('property_type')} className={inputCls}>
                                    <option value="detached">Detached</option>
                                    <option value="semi">Semi-detached</option>
                                    <option value="townhouse">Townhouse</option>
                                    <option value="condo">Condo</option>
                                    <option value="other">Other</option>
                                </select>
                            </Field>
                            <Field label="Timeline" required>
                                <select required data-testid="lc-timeline" value={form.timeline} onChange={set('timeline')} className={inputCls}>
                                    <option value="0-3-months">0–3 months</option>
                                    <option value="3-6-months">3–6 months</option>
                                    <option value="6-12-months">6–12 months</option>
                                    <option value="12-plus-months">12+ months</option>
                                    <option value="just-exploring">Just exploring</option>
                                </select>
                            </Field>
                            <Field label="Current mortgage">
                                <select data-testid="lc-mortgage" value={form.current_mortgage} onChange={set('current_mortgage')} className={inputCls}>
                                    <option value="prefer-not">Prefer not to say</option>
                                    <option value="none">None (fully paid)</option>
                                    <option value="under-250k">Under $250k</option>
                                    <option value="250k-500k">$250k – $500k</option>
                                    <option value="500k-1m">$500k – $1M</option>
                                    <option value="1m-plus">$1M+</option>
                                </select>
                            </Field>
                            <Field label="Preferred time">
                                <select data-testid="lc-time" value={form.preferred_time} onChange={set('preferred_time')} className={inputCls}>
                                    <option value="weekday-morning">Weekday morning</option>
                                    <option value="weekday-evening">Weekday evening</option>
                                    <option value="weekend">Weekend</option>
                                </select>
                            </Field>
                            <Field label="What&apos;s driving the sale?" required className="sm:col-span-2">
                                <textarea required rows={3} data-testid="lc-motivation" value={form.motivation} onChange={set('motivation')} className={inputCls} placeholder="Upsize, downsize, relocation, life change, investor divestment…" />
                            </Field>
                            <Field label="Anything else we should know?" className="sm:col-span-2">
                                <textarea rows={2} data-testid="lc-notes" value={form.notes} onChange={set('notes')} className={inputCls} placeholder="Recent renovations, timing constraints, other properties involved…" />
                            </Field>
                        </div>

                        {err && <div className="mt-4 text-[13px] text-red-600" data-testid="lc-error">{err}</div>}

                        <button
                            type="submit"
                            disabled={state === 'sending'}
                            data-testid="lc-submit"
                            className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-brand-navy text-white px-6 py-4 text-[14px] font-medium hover:bg-neutral-800 disabled:opacity-60"
                            style={{ letterSpacing: '0.02em' }}
                        >
                            {state === 'sending' ? 'Sending…' : (<>Book the consultation <ArrowUpRight className="w-4 h-4" /></>)}
                        </button>
                        <div className="mt-4 text-[11.5px] text-neutral-500 leading-relaxed">
                            We&apos;ll confirm the time by email within one business day. No cost, no
                            obligation, and no listing agreement discussed on the first visit.
                        </div>
                    </motion.form>
                </div>
            </div>
        </div>
    );
}

const inputCls = 'w-full border border-neutral-300 bg-white px-3 py-2.5 text-[14px] font-editorial focus:outline-none focus:border-black transition-colors';

function Field({ label, required, className, children }) {
    return (
        <label className={`block ${className || ''}`}>
            <div className="text-[10.5px] tracking-[0.2em] uppercase text-neutral-500 font-mono-data mb-1.5">
                {label}{required && ' *'}
            </div>
            {children}
        </label>
    );
}

function DoneState({ name }) {
    return (
        <div className="bg-white min-h-screen">
            <div className="max-w-[820px] mx-auto px-6 lg:px-10 pt-32 pb-24">
                <div className="text-[11px] tracking-[0.24em] uppercase text-neutral-500 font-mono-data flex items-center gap-3">
                    <span className="inline-block w-6 h-px bg-neutral-400" />
                    Request received
                </div>
                <h1 className="font-editorial mt-6 leading-[1.05] text-black" data-testid="lc-done-title" style={{ fontSize: 'clamp(36px, 5.5vw, 64px)', letterSpacing: '-0.03em' }}>
                    Thank you{name ? `, ${name.split(' ')[0]}` : ''}.
                    <br />
                    <span className="italic text-neutral-500">We&apos;ll be in touch within one business day.</span>
                </h1>
                <p className="mt-6 font-editorial text-neutral-700 max-w-2xl" style={{ fontSize: '18px', lineHeight: 1.55 }}>
                    We&apos;ll confirm the meeting time by email and send a short pre-read so the
                    thirty minutes are useful from the first sentence.
                </p>
                <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-0 border-l border-t border-neutral-200 max-w-2xl">
                    <Link to="/seller-centre" className="border-r border-b border-neutral-200 p-6 hover:bg-brand-navy hover:text-white transition-colors group">
                        <div className="overline">While you wait</div>
                        <div className="font-editorial mt-2 text-[20px] group-hover:italic">The twelve pillars</div>
                        <div className="mt-3 text-[12px] flex items-center gap-1">Open <ArrowUpRight className="w-3 h-3" /></div>
                    </Link>
                    <Link to="/sellers/home-value-report" className="border-r border-b border-neutral-200 p-6 hover:bg-brand-navy hover:text-white transition-colors group">
                        <div className="overline">Also useful</div>
                        <div className="font-editorial mt-2 text-[20px] group-hover:italic">Request the home value report</div>
                        <div className="mt-3 text-[12px] flex items-center gap-1">Request <ArrowUpRight className="w-3 h-3" /></div>
                    </Link>
                </div>
            </div>
        </div>
    );
}
